/**
 * The numbered loader's three wheels (hundreds, tens, units) as continuous
 * positions 0 → 10 (10 being the wrapped 0 at the bottom of a wheel) for a
 * value 0 → 100. Like a mechanical odometer: the units wheel turns with
 * the value, the tens wheel turns while the units pass from 9 to 0, the
 * hundreds wheel while the tens do. So no digit ever snaps.
 */
export function odometerWheels(value: number): [number, number, number] {
    const v = Math.max(0, Math.min(100, value));
    if (v >= 100) return [1, 0, 0];
    const units = v % 10;
    const tensValue = Math.floor(v / 10) + Math.max(0, units - 9);
    const tens = tensValue % 10;
    const hundreds = Math.max(0, tensValue - 9);
    return [hundreds, tens, units];
}

/** One wheel's position at an animation offset (0 → 1). */
export interface WheelKeyframe {
    offset: number;
    position: number;
}

/**
 * A wheel's positions along a sampled value trajectory (evenly spaced
 * samples), as keyframes for a linear Web Animation. A wheel that
 * completes a turn between two samples passes 10 and continues from 0:
 * the same face, so two keyframes at that one offset (10, then 0) make
 * the jump between frames rather than a roll backwards.
 */
export function odometerKeyframes(values: number[], wheel: 0 | 1 | 2): WheelKeyframe[] {
    const last = Math.max(1, values.length - 1);
    let prev = odometerWheels(values[0])[wheel];
    const frames: WheelKeyframe[] = [{ offset: 0, position: prev }];
    for (let i = 1; i < values.length; i++) {
        const pos = odometerWheels(values[i])[wheel];
        if (pos < prev - 5) {
            const span = (10 - prev) + pos;
            const at = (i - 1 + (span > 0 ? (10 - prev) / span : 0)) / last;
            frames.push({ offset: at, position: 10 }, { offset: at, position: 0 });
        }
        frames.push({ offset: i / last, position: pos });
        prev = pos;
    }
    if (values.length === 1) frames.push({ offset: 1, position: prev });
    return frames;
}
